import { useEffect, useRef, useState } from "react";

import type { TabType } from "@/hooks/useDashboardTabs";
import {
  markDashboardMounted,
  shouldSkipDashboardBootRefresh,
} from "@/lib/bootHint";
import { useAppDispatch } from "@/redux/hooks";
import { enhancedApi } from "@/redux/services/enhancedApi";

type UseDashboardMountRefreshArgs = {
  /** `refetch` of the dashboard's balances query (`useAccountSummary`). */
  refetchBalances: () => unknown;
  /** The balances query's `isUninitialized`. */
  isBalancesUninitialized: boolean;
  activeTab: TabType;
};

/**
 * The dashboard's refresh-on-visit: refetch balances when it mounts, and
 * invalidate the payment lists and ledger on the first mount, then the active
 * tab's list on every tab change.
 *
 * On the first dashboard mount since page load, while this page load's boot
 * prefetch (`lib/bootHint.ts`) is still fresh, the mount refetch and the
 * first-mount invalidation are skipped: that data was fetched moments ago for
 * this boot, and refetching it would send every boot read twice. Later visits
 * and tab changes refresh exactly as before.
 */
export function useDashboardMountRefresh({
  refetchBalances,
  isBalancesUninitialized,
  activeTab,
}: UseDashboardMountRefreshArgs) {
  const dispatch = useAppDispatch();
  // Fixed for this mount; the read is pure, so a doubled initialiser is safe.
  const [skipBootRefresh] = useState(() => shouldSkipDashboardBootRefresh());

  useEffect(() => {
    markDashboardMounted();
  }, []);

  useEffect(() => {
    if (isBalancesUninitialized || skipBootRefresh) return;
    refetchBalances();
  }, [refetchBalances, isBalancesUninitialized, skipBootRefresh]);

  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      if (skipBootRefresh) return;
      // "PaymentLists", not the generated catch-all "core": "core" also
      // evicts the recipients list, which the dashboard never shows.
      dispatch(
        enhancedApi.util.invalidateTags(["PaymentLists", "LedgerEntries"])
      );
      return;
    }
    dispatch(
      enhancedApi.util.invalidateTags(
        activeTab === "activity" ? ["LedgerEntries"] : ["PaymentLists"]
      )
    );
  }, [dispatch, activeTab, skipBootRefresh]);
}
